import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import plusCircleIconTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import { css } from '@emotion/css';
import { describeRequestFailure, useSubmitCatalogJob } from '../hooks/useCatalogJob';
import ActionButton from './ActionButton';
import useSurfaceStyles from '../hooks/useSurfaceStyles';
import { CatalogJobLocationState, catalogJobHref } from '../utils/catalog-job-route';

interface OnboardServiceModalProps {
  open: boolean;
  onClose: () => void;
}

const ONBOARDING_STEPS = [
  'A catalog job is submitted for the service',
  'MCP tool schemas are generated for each API',
  'You land on the job status page to track progress',
];

const MODAL_CONTENT_INSET = 24;
const MERIDIAN_MODAL_CLOSE_BUTTON_WIDTH = 32;

// Without a Meridian title, Meridian pads the whole body's end side by the close button's width
// (modal/modal-box.js), which pushes the input and steps off-center. Pulling the content back over
// that reserved space keeps both sides at the same inset; only the header needs to clear the button.
const modalContentClass = css({
  marginInlineEnd: -MERIDIAN_MODAL_CLOSE_BUTTON_WIDTH,
  paddingInlineEnd: MODAL_CONTENT_INSET,
});

const headerClass = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
  paddingInlineEnd: MERIDIAN_MODAL_CLOSE_BUTTON_WIDTH,
});

const stepListClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

const stepClass = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const stepsLabelClass = css({
  marginBottom: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
});

export default function OnboardServiceModal({ open, onClose }: OnboardServiceModalProps) {
  const navigate = useNavigate();
  const meridianTheme = useMeridianTheme();
  const { accentIconClass, accentTintColor } = useSurfaceStyles();
  const stepsPanelClass = css({
    padding: 16,
    borderRadius: 12,
    border: `1px solid ${meridianTheme('themeBorderSubdued')}`,
    backgroundColor: meridianTheme('themePageHighlight'),
  });
  const stepNumberClass = css({
    display: 'flex',
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: accentTintColor,
    color: meridianTheme('themeActionPrimaryDefault'),
    fontSize: 12,
    fontWeight: 600,
  });
  const [serviceName, setServiceName] = useState('');
  const [serviceNameError, setServiceNameError] = useState<string | null>(null);
  const submitCatalogJobMutation = useSubmitCatalogJob();
  const { mutate: submitCatalogJobRequest, reset: resetSubmission } = submitCatalogJobMutation;
  const isSubmitting = submitCatalogJobMutation.isLoading;
  const submitError =
    submitCatalogJobMutation.isError && submitCatalogJobMutation.variables
      ? describeRequestFailure(
          'submit the catalog job',
          submitCatalogJobMutation.variables.serviceName,
          submitCatalogJobMutation.error,
        )
      : null;

  useEffect(() => {
    if (!open) return;

    setServiceName('');
    setServiceNameError(null);
    resetSubmission();
  }, [open, resetSubmission]);

  const handleServiceNameChange = (value: string) => {
    setServiceName(value);
    if (serviceNameError && value.trim()) {
      setServiceNameError(null);
    }
  };

  const handleSubmit = () => {
    const normalizedServiceName = serviceName.trim();

    if (!normalizedServiceName) {
      setServiceNameError('Enter the service name to catalog.');
      return;
    }

    submitCatalogJobRequest(
      { serviceName: normalizedServiceName, forceSubmit: false },
      {
        onSuccess: (response) => {
          const locationState: CatalogJobLocationState = {
            submittedCatalogJob: { serviceName: normalizedServiceName, jobId: response.jobId },
          };
          navigate(catalogJobHref(normalizedServiceName), { state: locationState });
        },
      },
    );
  };

  // The modal title is rendered in the body instead of Meridian's title prop, which forces a dark
  // header bar and a dark-mode close button that would be invisible on a light header.
  return (
    <Modal
      open={open}
      onClose={onClose}
      onSubmitForm={handleSubmit}
      closeLabel="Close onboard service dialog"
      aria-labelledby="onboard-service-title"
      aria-describedby="onboard-service-description"
      width={520}
      bodySpacingInset="450 none 200 450"
    >
      <div className={modalContentClass}>
        <Column spacing="500">
          <div className={headerClass}>
            <span className={accentIconClass}>
              <Icon tokens={plusCircleIconTokens} />
            </span>
            <Column spacing="100">
              <Heading id="onboard-service-title" level={2} type="h300">
                Onboard a service
              </Heading>
              <Text id="onboard-service-description" type="b200" color="secondary">
                Submit a catalog job to generate tool schemas for MCP gateways and tools.
              </Text>
            </Column>
          </div>
          <Input
            label="Service name"
            value={serviceName}
            onChange={handleServiceNameChange}
            helperText="Use the exact service name"
            error={Boolean(serviceNameError)}
            errorMessage={serviceNameError || undefined}
            size="large"
            width="100%"
            autoComplete="off"
            inaccessibleAutoFocus
            required
          />
          <div className={stepsPanelClass}>
            <Text type="b100" color="secondary" className={stepsLabelClass}>
              What happens next
            </Text>
            <ol className={stepListClass}>
              {ONBOARDING_STEPS.map((step, position) => (
                <li key={step} className={stepClass}>
                  <span className={stepNumberClass}>{position + 1}</span>
                  <Text type="b200">{step}</Text>
                </li>
              ))}
            </ol>
          </div>
          {submitError && (
            <Alert type="error" size="medium">
              {submitError}
            </Alert>
          )}
        </Column>
      </div>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit" spacing="200">
          <ActionButton variant="secondary" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton variant="primary" type="submit" busy={isSubmitting} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit job'}
          </ActionButton>
        </Row>
      </ModalFooter>
    </Modal>
  );
}
